import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle2, CreditCard, DollarSign, Send, Copy as CopyIcon, X } from "lucide-react";
import { toast } from "sonner";

// Access Rules — single source of truth for the comparison table.
// Each row: [label, truth_keeper, truth_seeker, truth_maker]
const ACCESS_ROWS = [
  ["Weekly Truth", true, true, true],
  ["Story Behind The Piece", true, true, true],
  ["Audio Version Read By Mickey", true, true, true],
  ["Mobile Wallpaper + Printable", true, true, true],
  ["Early Access to New Releases", true, true, true],
  ["Complete Truth Archive", false, true, true],
  ["Members-Only Art Releases", false, true, true],
  ["Behind-The-Scenes Video (Monthly)", false, true, true],
  ["Downloadable Quote Collection", false, true, true],
  ["Members-Only Community Polls", false, true, true],
  ["Vote On Future Truth Topics", false, true, true],
  ["Quarterly Exclusive Print", false, true, true],
  ["Limited Edition Truth Maker Releases", false, false, true],
  ["Monthly Live Q&A With Mickey", false, false, true],
  ["Name On The Founding Wall", false, false, true],
  ["First Access To New Collections", false, false, true],
  ["Exclusive Truth Maker Merchandise", false, false, true],
  ["Annual Signed Print", false, false, true],
  ["Suggest Future Truth Concepts", false, false, true],
  ["Private Truth Maker Circle", false, false, true],
];

export default function Subscriptions() {
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [picker, setPicker] = useState(null);
  const [manualResult, setManualResult] = useState(null);

  useEffect(() => {
    http.get("/subscription-plans").then(({ data }) => setPlans(data));
  }, []);
  useEffect(() => { if (user?.email) setEmail(user.email); }, [user]);

  const startCheckout = async (plan, payment_method) => {
    if (!email) { toast.error("Enter your email first"); return; }
    setLoading(true);
    try {
      const { data } = await http.post("/subscriptions/checkout", {
        plan_id: plan.id,
        billing_cycle: cycle,
        origin_url: window.location.origin,
        email,
        payment_method,
        name: user?.name || undefined,
      });
      if (payment_method === "stripe") {
        if (data.url) { window.location.href = data.url; return; }
      } else {
        setManualResult({ ...data, plan });
        setPicker(null);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start subscription");
    } finally { setLoading(false); }
  };

  const sortedPlans = useMemo(
    () => [...plans].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [plans]
  );

  return (
    <Layout>
      {/* HERO */}
      <section className="bg-[#050505] pt-20 pb-14 border-b border-white/5" data-testid="subscriptions-hero">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 text-center">
          <div className="font-ui text-[11px] tracking-[0.32em] text-[#C9A055] mb-4">JOIN THE CLUB</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F4F1EA] leading-[1.05]">
            ORDINARY THINGS.<br/>
            <span className="text-[#C9A055]">EXTRAORDINARY LESSONS.</span>
          </h1>
          <p className="font-body text-[#B5B0A7] max-w-2xl mx-auto mt-6 leading-relaxed">
            A weekly reminder for the people still climbing. Pick the tier that matches how deep you want to go.
          </p>

          {/* Billing-cycle toggle */}
          <div className="inline-flex border border-white/10 mt-10 p-1 bg-[#0a0a0a]" data-testid="billing-toggle">
            <button
              onClick={() => setCycle("monthly")}
              data-testid="billing-monthly"
              className={`px-6 py-2 font-ui text-[10px] tracking-[0.32em] uppercase transition-colors ${cycle === "monthly" ? "bg-[#C9A055] text-black" : "text-[#9a9690] hover:text-[#F4F1EA]"}`}
            >
              Monthly
            </button>
            <button
              onClick={() => setCycle("yearly")}
              data-testid="billing-yearly"
              className={`px-6 py-2 font-ui text-[10px] tracking-[0.32em] uppercase transition-colors relative ${cycle === "yearly" ? "bg-[#C9A055] text-black" : "text-[#9a9690] hover:text-[#F4F1EA]"}`}
            >
              Yearly
              <span className="absolute -top-2 -right-3 bg-[#5fc377] text-black font-ui text-[8px] tracking-[0.15em] px-1.5 py-0.5">SAVE 2MO</span>
            </button>
          </div>
        </div>
      </section>

      {/* PLANS */}
      <section className="bg-[#050505] py-16">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          {!user && (
            <div className="border border-white/10 bg-[#0a0a0a] p-4 mb-8 flex flex-col sm:flex-row gap-3 items-center justify-between">
              <p className="font-body text-sm text-[#B5B0A7]">Enter your email — we'll create your account when you subscribe.</p>
              <input value={email} onChange={e => setEmail(e.target.value)} type="email" required placeholder="you@email.com" className="stc-input max-w-xs" data-testid="sub-email"/>
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-5" data-testid="plans-grid">
            {sortedPlans.map(p => {
              const price = cycle === "yearly" ? p.yearly_price : p.monthly_price;
              const period = cycle === "yearly" ? "year" : "month";
              const equivMonthly = cycle === "yearly" ? (p.yearly_price / 12).toFixed(2) : null;
              return (
                <div
                  key={p.id}
                  data-testid={`plan-${p.tier_slug}`}
                  className={`relative border bg-[#0a0a0a] p-7 flex flex-col ${p.featured ? "border-[#C9A055]" : "border-white/10"}`}
                >
                  {p.featured && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C9A055] text-black font-ui text-[10px] tracking-[0.32em] px-3 py-1">MOST POPULAR</div>
                  )}
                  <h2 className="font-display text-3xl text-[#F4F1EA]">{p.name.toUpperCase()}</h2>
                  <p className="font-body text-sm text-[#B5B0A7] mt-2 min-h-[40px]">{p.tagline || p.description}</p>
                  <div className="flex items-baseline gap-1 mt-5">
                    <span className="font-display text-5xl text-[#C9A055]">${price.toFixed(0)}</span>
                    <span className="font-ui text-xs tracking-[0.22em] text-[#B5B0A7]">/{period}</span>
                  </div>
                  {equivMonthly && (
                    <div className="font-ui text-[10px] tracking-[0.22em] text-[#5fc377] mt-1">${equivMonthly}/mo equivalent · 2 months free</div>
                  )}
                  <ul className="space-y-2.5 mt-6 mb-7 flex-1">
                    {(p.perks || []).map((perk, i) => (
                      <li key={i} className="flex gap-2 items-start font-body text-[14px] text-[#F4F1EA]">
                        <CheckCircle2 size={16} className="text-[#C9A055] flex-shrink-0 mt-0.5"/>
                        <span>{perk}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    data-testid={`sub-btn-${p.tier_slug}`}
                    disabled={loading}
                    onClick={() => setPicker(p)}
                    className={`${p.featured ? "btn-gold" : "btn-outline"} w-full`}
                  >
                    {loading ? "..." : "Join Now"}
                  </button>
                  <p className="text-center font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mt-3">Cancel anytime.</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ACCESS RULES */}
      <section className="bg-[#050505] pb-20 pt-4">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <div className="font-ui text-[11px] tracking-[0.32em] text-[#C9A055]">ACCESS RULES</div>
            <h2 className="font-display text-3xl md:text-4xl text-[#F4F1EA] mt-2">What's Included</h2>
            <p className="font-body text-[#B5B0A7] mt-3 max-w-xl mx-auto">Every tier inherits everything below it. Move up when you're ready to go deeper.</p>
          </div>
          <div className="border border-white/10 bg-[#0a0a0a] overflow-x-auto" data-testid="access-rules-table">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 font-ui text-[10px] tracking-[0.32em] text-[#8a857d] uppercase">Feature</th>
                  <th className="text-center p-4 font-display text-base text-[#F4F1EA]">Truth Keeper</th>
                  <th className="text-center p-4 font-display text-base text-[#C9A055]">Truth Seeker</th>
                  <th className="text-center p-4 font-display text-base text-[#F4F1EA]">Truth Maker</th>
                </tr>
              </thead>
              <tbody>
                {ACCESS_ROWS.map(([label, k, s, m], i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="p-4 font-body text-[13px] text-[#F4F1EA]">{label}</td>
                    <Cell on={k}/>
                    <Cell on={s} accent/>
                    <Cell on={m}/>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Payment method picker */}
      {picker && (
        <PaymentMethodPicker
          plan={picker}
          cycle={cycle}
          price={cycle === "yearly" ? picker.yearly_price : picker.monthly_price}
          loading={loading}
          onPick={(method) => startCheckout(picker, method)}
          onClose={() => setPicker(null)}
        />
      )}

      {manualResult && (
        <ManualInstructionsModal data={manualResult} onClose={() => setManualResult(null)} />
      )}
    </Layout>
  );
}

function Cell({ on, accent }) {
  return (
    <td className={`p-4 text-center ${accent ? "bg-[#C9A055]/5" : ""}`}>
      {on
        ? <CheckCircle2 size={18} className="inline text-[#C9A055]"/>
        : <span className="text-[#3a3631]">—</span>}
    </td>
  );
}

function PaymentMethodPicker({ plan, cycle, price, loading, onPick, onClose }) {
  const methods = [
    { key: "stripe",  label: "Card (Stripe)", icon: CreditCard, sub: "Visa · Mastercard · Amex" },
    { key: "cashapp", label: "Cash App",       icon: DollarSign, sub: "$MickeyTrivett" },
    { key: "venmo",   label: "Venmo",          icon: Send,        sub: "@MickeyTrivett" },
  ];
  return (
    <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="sub-method-picker">
      <div onClick={e => e.stopPropagation()} className="bg-[#0a0a0a] border border-[#C9A055]/40 w-full max-w-md p-7">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">Choose how you'd like to pay</div>
            <div className="font-display text-2xl text-[#F4F1EA] mt-1">{plan.name.toUpperCase()} · ${price.toFixed(0)}/{cycle === "yearly" ? "year" : "month"}</div>
          </div>
          <button onClick={onClose} className="text-[#8a857d] hover:text-[#F4F1EA]" aria-label="Close" data-testid="sub-picker-close"><X size={20}/></button>
        </div>
        <div className="space-y-2.5">
          {methods.map(m => (
            <button
              key={m.key}
              disabled={loading}
              onClick={() => onPick(m.key)}
              className="w-full flex items-center gap-4 border border-white/10 hover:border-[#C9A055] bg-[#080808] px-4 py-3.5 text-left transition-colors group disabled:opacity-50"
              data-testid={`sub-method-${m.key}`}
            >
              <div className="w-10 h-10 border border-[#C9A055]/50 text-[#C9A055] group-hover:bg-[#C9A055] group-hover:text-black flex items-center justify-center transition-colors">
                <m.icon size={16}/>
              </div>
              <div className="flex-1">
                <div className="font-display text-lg text-[#F4F1EA]">{m.label}</div>
                <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] uppercase">{m.sub}</div>
              </div>
              <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] group-hover:translate-x-1 transition-transform">→</div>
            </button>
          ))}
        </div>
        <p className="font-body text-[11px] text-[#6a6660] mt-5 leading-relaxed">Card payments activate your subscription instantly. Cash App and Venmo activate within 24h after Mickey confirms payment.</p>
      </div>
    </div>
  );
}

function ManualInstructionsModal({ data, onClose }) {
  const copy = (txt) => { navigator.clipboard.writeText(txt).then(() => toast.success("Copied.")); };
  return (
    <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="sub-manual-modal">
      <div onClick={e => e.stopPropagation()} className="bg-[#0a0a0a] border border-[#C9A055]/40 w-full max-w-lg p-7">
        <div className="flex items-center justify-between mb-4">
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">Send your first payment</div>
          <button onClick={onClose} className="text-[#8a857d] hover:text-[#F4F1EA]" aria-label="Close" data-testid="sub-manual-close"><X size={20}/></button>
        </div>
        <div className="font-display text-3xl text-[#F4F1EA] mb-2">{data.plan?.name?.toUpperCase()}</div>
        <div className="font-script text-[#C9A055] text-2xl mb-6">Almost there.</div>

        <div className="space-y-3">
          <div className="border border-white/10 p-4">
            <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">Amount</div>
            <div className="flex items-center justify-between">
              <div className="font-display text-3xl text-[#C9A055]">${data.amount?.toFixed(2)}</div>
              <button onClick={() => copy(data.amount.toFixed(2))} className="btn-outline text-xs" data-testid="copy-amount"><CopyIcon size={11}/> Copy</button>
            </div>
          </div>
          <div className="border border-white/10 p-4">
            <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">Send to · {data.label}</div>
            <div className="flex items-center justify-between">
              <div className="font-display text-2xl text-[#F4F1EA]">{data.handle}</div>
              <button onClick={() => copy(data.handle)} className="btn-outline text-xs" data-testid="copy-handle"><CopyIcon size={11}/> Copy</button>
            </div>
          </div>
          <div className="border border-[#C9A055]/40 p-4 bg-[#0e0c08]">
            <div className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055]">Include this reference in the payment note</div>
            <div className="flex items-center justify-between">
              <div className="font-mono text-xl text-[#F4F1EA]">{data.reference}</div>
              <button onClick={() => copy(data.reference)} className="btn-outline text-xs border-[#C9A055] text-[#C9A055]" data-testid="copy-reference"><CopyIcon size={11}/> Copy</button>
            </div>
            <p className="font-body text-[11px] text-[#8a857d] mt-2">Without this reference Mickey can't match your payment to your subscription.</p>
          </div>
        </div>

        <div className="mt-6 font-body text-[13px] text-[#B5B0A7] leading-relaxed border-t border-white/10 pt-5">
          Your subscription will activate within 24 hours after Mickey confirms your payment. You'll get an email when it's live, and you can track status in your <Link to="/account" className="text-[#C9A055] hover:underline">account</Link>.
        </div>
        <button onClick={onClose} className="btn-gold w-full mt-5" data-testid="sub-manual-done">Got it</button>
      </div>
    </div>
  );
}
